import React, { useEffect, useState } from "react";
import { Save, ArrowUp, ArrowDown, Eye, EyeOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { invalidateSiteSettings } from "@/hooks/useSiteSettings";

const TAB_LIST = [
  { key: "hero", label: "Hero" },
  { key: "sections", label: "Homepage Sections" },
  { key: "ticker", label: "Ticker" },
  { key: "partners", label: "Partners" },
  { key: "statistics", label: "Statistics" },
  { key: "footer", label: "Footer" },
  { key: "contact", label: "Contact Info" },
];

const AdminSiteSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("hero");

  const load = async () => {
    try {
      const s = await api.siteSettings();
      setSettings(s);
    } catch { toast.error("Failed to load settings"); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const fresh = await api.adminUpdateSiteSettings(settings);
      setSettings(fresh);
      invalidateSiteSettings(fresh);
      toast.success("Settings published — live on the site.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!settings) return <div className="text-ink-soft">Loading settings…</div>;

  const setHomepage = (patch) => setSettings((s) => ({ ...s, homepage: { ...(s.homepage || {}), ...patch } }));
  const setHero = (patch) => setHomepage({ hero: { ...(settings.homepage?.hero || {}), ...patch } });
  const setFooter = (patch) => setSettings((s) => ({ ...s, footer: { ...(s.footer || {}), ...patch } }));
  const setContact = (patch) => setSettings((s) => ({ ...s, contact: { ...(s.contact || {}), ...patch } }));

  const sections = settings.homepage?.sections || [];
  const moveSection = (idx, dir) => {
    const next = [...sections];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setHomepage({ sections: next });
  };
  const toggleSection = (idx) => {
    const next = sections.map((s, i) => i === idx ? { ...s, enabled: !s.enabled } : s);
    setHomepage({ sections: next });
  };
  const updateSection = (idx, patch) => {
    const next = sections.map((s, i) => i === idx ? { ...s, ...patch } : s);
    setHomepage({ sections: next });
  };

  return (
    <div data-testid="admin-site-settings-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline overline-muted mb-3">Site</div>
          <h1 className="font-display text-ink text-4xl md:text-5xl leading-[1.05] tracking-[-0.015em]">Site Settings</h1>
          <p className="mt-2 text-ink-soft text-sm max-w-xl">Edit the homepage content, section order, ticker, partners, footer and contact info. Publish once to push live.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex items-center gap-1 text-xs text-ink-soft hover:text-ink border border-ink-line rounded-full px-4 py-2" data-testid="ss-reload">
            <RefreshCw size={13} /> Reload
          </button>
          <button onClick={save} disabled={saving} data-testid="ss-save-publish" className="inline-flex items-center gap-2 px-6 py-2.5 bg-ink text-paper text-xs font-medium rounded-full hover:bg-gold-dark transition-colors disabled:opacity-40">
            <Save size={13} /> {saving ? "Publishing…" : "Save & Publish"}
          </button>
        </div>
      </div>

      <div className="mt-8 border-b border-ink-line overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {TAB_LIST.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`ss-tab-${t.key}`}
              className={`px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-mono-editorial transition-colors ${
                tab === t.key ? "text-ink border-b-2 border-ink -mb-px" : "text-ink-muted hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8">
        {tab === "hero" && (
          <div className="editorial-card p-6 md:p-8 space-y-5 max-w-3xl">
            <Field label="Headline (HTML — wrap emphasized words in <em>...</em>)" as="textarea" rows={3} value={settings.homepage.hero.headline_html} onChange={(v) => setHero({ headline_html: v })} testId="ss-hero-headline" />
            <Field label="Sub-headline" as="textarea" rows={3} value={settings.homepage.hero.subhead} onChange={(v) => setHero({ subhead: v })} testId="ss-hero-subhead" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Primary CTA label" value={settings.homepage.hero.cta_primary} onChange={(v) => setHero({ cta_primary: v })} testId="ss-hero-cta1" />
              <Field label="Primary CTA link" value={settings.homepage.hero.cta_primary_href} onChange={(v) => setHero({ cta_primary_href: v })} testId="ss-hero-cta1-href" />
              <Field label="Secondary CTA label" value={settings.homepage.hero.cta_secondary} onChange={(v) => setHero({ cta_secondary: v })} testId="ss-hero-cta2" />
              <Field label="Secondary CTA link" value={settings.homepage.hero.cta_secondary_href} onChange={(v) => setHero({ cta_secondary_href: v })} testId="ss-hero-cta2-href" />
            </div>
            <ToggleRow label="Show cover teaser card in hero" value={!!settings.homepage.hero.show_teaser} onChange={(v) => setHero({ show_teaser: v })} testId="ss-hero-teaser-toggle" />
          </div>
        )}

        {tab === "sections" && (
          <div className="space-y-4">
            <p className="text-ink-soft text-sm max-w-2xl">Reorder homepage sections with the arrows, toggle visibility with the eye, and edit the overline / title / lede shown above each one.</p>
            {sections.map((s, idx) => (
              <div key={s.key} className={`editorial-card p-5 md:p-6 ${s.enabled ? "" : "opacity-50"}`} data-testid={`ss-section-${s.key}`}>
                <div className="flex items-start gap-4">
                  <div className="flex flex-col gap-1">
                    <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="w-8 h-8 border border-ink-line rounded-full flex items-center justify-center text-ink-soft hover:text-ink hover:border-ink disabled:opacity-30" data-testid={`ss-section-up-${s.key}`} aria-label="Move up"><ArrowUp size={13} /></button>
                    <button onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1} className="w-8 h-8 border border-ink-line rounded-full flex items-center justify-center text-ink-soft hover:text-ink hover:border-ink disabled:opacity-30" data-testid={`ss-section-down-${s.key}`} aria-label="Move down"><ArrowDown size={13} /></button>
                  </div>
                  <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                    <div className="md:col-span-3">
                      <div className="overline overline-muted mb-1">Section</div>
                      <div className="font-display text-ink text-base leading-tight">{s.key.replace(/_/g, " ")}</div>
                      <div className="mt-2 font-mono-editorial text-[11px] text-ink-muted">Position {idx + 1}</div>
                    </div>
                    <div className="md:col-span-9 grid grid-cols-1 gap-3">
                      <Field label="Overline" value={s.overline || ""} onChange={(v) => updateSection(idx, { overline: v })} testId={`ss-section-${s.key}-overline`} />
                      <Field label="Title" value={s.title || ""} onChange={(v) => updateSection(idx, { title: v })} testId={`ss-section-${s.key}-title`} />
                      <Field label="Lede" as="textarea" rows={2} value={s.lede || ""} onChange={(v) => updateSection(idx, { lede: v })} testId={`ss-section-${s.key}-lede`} />
                    </div>
                  </div>
                  <button onClick={() => toggleSection(idx)} data-testid={`ss-section-toggle-${s.key}`} className="w-9 h-9 border border-ink-line rounded-full flex items-center justify-center text-ink-soft hover:text-ink hover:border-ink" aria-label={s.enabled ? "Hide" : "Show"}>
                    {s.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "ticker" && (
          <div className="editorial-card p-6 md:p-8 max-w-2xl">
            <Field
              label="Ticker keywords (one per line — appears in the scrolling ribbon on the homepage)"
              as="textarea"
              rows={12}
              value={(settings.homepage.hero.ticker || []).join("\n")}
              onChange={(v) => setHero({ ticker: v.split("\n").map((x) => x.trim()).filter(Boolean) })}
              testId="ss-ticker"
            />
          </div>
        )}

        {tab === "partners" && (
          <div className="editorial-card p-6 md:p-8 max-w-2xl">
            <Field
              label="Partners (one per line)"
              as="textarea"
              rows={10}
              value={(settings.homepage.partners || []).join("\n")}
              onChange={(v) => setHomepage({ partners: v.split("\n").map((x) => x.trim()).filter(Boolean) })}
              testId="ss-partners"
            />
          </div>
        )}

        {tab === "statistics" && (
          <div className="editorial-card p-6 md:p-8 space-y-4 max-w-2xl">
            <p className="overline overline-muted">Four statistics shown on the homepage.</p>
            {(settings.homepage.statistics || []).map((st, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-5"><Field label={`#${i + 1} Label`} value={st.label} onChange={(v) => {
                  const arr = [...settings.homepage.statistics]; arr[i] = { ...arr[i], label: v }; setHomepage({ statistics: arr });
                }} testId={`ss-stat-${i}-label`} /></div>
                <div className="md:col-span-4"><Field label="Value" type="number" value={st.value} onChange={(v) => {
                  const arr = [...settings.homepage.statistics]; arr[i] = { ...arr[i], value: Number(v) || 0 }; setHomepage({ statistics: arr });
                }} testId={`ss-stat-${i}-value`} /></div>
                <div className="md:col-span-3"><Field label="Suffix" value={st.suffix || ""} onChange={(v) => {
                  const arr = [...settings.homepage.statistics]; arr[i] = { ...arr[i], suffix: v }; setHomepage({ statistics: arr });
                }} testId={`ss-stat-${i}-suffix`} /></div>
              </div>
            ))}
          </div>
        )}

        {tab === "footer" && (
          <div className="editorial-card p-6 md:p-8 space-y-5 max-w-2xl">
            <Field label="Footer description" as="textarea" rows={4} value={settings.footer.description} onChange={(v) => setFooter({ description: v })} testId="ss-footer-desc" />
            <Field label="Copyright text (year is auto-prefixed)" value={settings.footer.copyright} onChange={(v) => setFooter({ copyright: v })} testId="ss-footer-copyright" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {["twitter","linkedin","instagram","youtube"].map((k) => (
                <Field key={k} label={`${k.charAt(0).toUpperCase() + k.slice(1)} URL`} value={settings.footer.social_links?.[k] || ""} onChange={(v) => setFooter({ social_links: { ...(settings.footer.social_links || {}), [k]: v } })} testId={`ss-footer-social-${k}`} />
              ))}
            </div>
          </div>
        )}

        {tab === "contact" && (
          <div className="editorial-card p-6 md:p-8 space-y-5 max-w-2xl">
            <Field label="Public email" value={settings.contact.email} onChange={(v) => setContact({ email: v })} testId="ss-contact-email" />
            <Field label="Public phone" value={settings.contact.phone} onChange={(v) => setContact({ phone: v })} testId="ss-contact-phone" />
            <Field label="Office / location" value={settings.contact.office} onChange={(v) => setContact({ office: v })} testId="ss-contact-office" />
            <Field label="Google Maps embed URL (leave empty to keep the stylised placeholder map)" value={settings.contact.map_embed_url || ""} onChange={(v) => setContact({ map_embed_url: v })} testId="ss-contact-map" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {["twitter","linkedin","instagram","youtube"].map((k) => (
                <Field key={k} label={`${k.charAt(0).toUpperCase() + k.slice(1)} URL`} value={settings.contact.social_links?.[k] || ""} onChange={(v) => setContact({ social_links: { ...(settings.contact.social_links || {}), [k]: v } })} testId={`ss-contact-social-${k}`} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, as = "input", type = "text", rows = 3, testId }) => (
  <div>
    <label className="overline overline-muted block mb-2">{label}</label>
    {as === "textarea" ? (
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full px-3 py-2.5 bg-paper border border-ink-line rounded text-ink text-sm outline-none focus:border-ink resize-y" />
    ) : (
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full px-3 py-2.5 bg-paper border border-ink-line rounded text-ink text-sm outline-none focus:border-ink" />
    )}
  </div>
);

const ToggleRow = ({ label, value, onChange, testId }) => (
  <label className="flex items-center gap-3 cursor-pointer select-none">
    <button type="button" onClick={() => onChange(!value)} data-testid={testId} className={`w-11 h-6 rounded-full border transition-colors relative ${value ? "bg-ink border-ink" : "bg-paper border-ink-line"}`} aria-pressed={value}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${value ? "left-6 bg-paper" : "left-0.5 bg-ink"}`} />
    </button>
    <span className="text-sm text-ink">{label}</span>
  </label>
);

export default AdminSiteSettingsPage;
